/**
 * Trade-in types for the partner-facing Trade-ins page.
 *
 * Derived from the generated admin schema (`RewareAdminTypes`, regenerated from reware-api)
 * rather than hand-transcribed. The hand-written versions these replace disagreed with the
 * schema in ways that each shipped as a runtime bug rather than a compile error: `uuidPrefix`
 * declared non-null when it is nullable, every `[Type]` list declared `T[]` when the wire sends
 * a nullable list of nullable elements, the page envelope declared non-null throughout, and two
 * filters typed as strings when they are an Int and an enum.
 *
 * Only deliberate narrowings are spelled out below. Anything aliased straight across is the
 * generated type verbatim, so a schema change surfaces here as a type error.
 *
 * Amounts are in cents on the wire.
 */
import { z } from 'zod';

import type { RewareAdminTypes } from '@troverecommerce/shared-reware-api';

import type { ConstantOption } from '@src/components/indexPage';

export type { ConstantOption };

export type Ypackage = RewareAdminTypes.YpackageAdmin;
export type Escalation = RewareAdminTypes.EscalationAdmin;
export type TradeinEmail = RewareAdminTypes.EmailHistoryAdmin;
export type TradeinStateTransition = RewareAdminTypes.StateTransitionsAdmin;
export type GiftcardTotals = RewareAdminTypes.GiftcardTotals;

/**
 * A gift card issued against the trade-in, narrowed to the fields the query selects.
 *
 * Deliberately NOT the whole `GiftcardAdmin`, which declares `number: String!`. Reached through
 * `tradein.giftcards` that number is unmasked and unaudited — see the note on the selection in
 * fetchTradeins.ts — and picking only these means the type system refuses any attempt to read it
 * from this page rather than leaving it one autocomplete away.
 */
export type Giftcard = Pick<
  RewareAdminTypes.GiftcardAdmin,
  'id' | 'amount' | 'lastFour' | 'source' | 'vendorSource' | 'state'
>;

/**
 * Accepts a number where a string is expected. Postal codes in particular arrive as numbers for
 * some partners, and a strict `z.string()` would turn one numeric postcode into a failed parse
 * for the whole address — which blanks the Edit shipping address dialog, whose contents the
 * server then merges over the stored address. An avoidable rejection becomes a way to erase a
 * real address.
 *
 * A nested object or array still fails, which is the case that genuinely means "we cannot read
 * this".
 */
const addressField = z.union([z.string(), z.number()]).transform(String).nullish();

/**
 * `deliveryAddress` is a `SequelizeJSON` scalar, so the generated type is `any` and the schema
 * tells us nothing about its contents. Parsed rather than asserted: an interface here would be a
 * guess the compiler then treats as fact, and if the shape differs — keys absent rather than
 * null, or the scalar arriving as a JSON string — every `deliveryAddress?.x` read silently
 * yields undefined, so the Supplier column shows an em dash on every row and the address dialog
 * opens empty. The operator is told a fact about the supplier's data that is really a parse
 * failure.
 */
export const deliveryAddressSchema = z.object({
  name: addressField,
  firstName: addressField,
  lastName: addressField,
  address1: addressField,
  address2: addressField,
  city: addressField,
  state: addressField,
  postalCode: addressField,
  country: addressField,
  email: addressField,
  phone: addressField,
});

export type DeliveryAddress = z.infer<typeof deliveryAddressSchema>;

/**
 * Narrow an unparsed `deliveryAddress` to the fields the page reads.
 *
 * Returns null when the value is absent or unreadable, which callers must distinguish from a
 * parsed address whose fields are empty — the first means "we could not read this", the second
 * means "there is nothing recorded".
 */
export const parseDeliveryAddress = (value: unknown): DeliveryAddress | null => {
  if (value == null || typeof value !== 'object') return null;
  const parsed = deliveryAddressSchema.safeParse(value);
  if (!parsed.success) {
    console.error('Trade-in delivery address did not match the expected shape', {
      issues: parsed.error.issues,
    });
    return null;
  }
  return parsed.data;
};

/**
 * `imageUrls` is a JSON scalar, so the generated type is `any`. Partial because a given position
 * may be absent, which the item card relies on when it falls back to the first available image.
 */
export type ImageUrlMap = Partial<Record<string, string>>;

/**
 * Narrow the `imageUrls` scalar, dropping any entry that is not a string URL.
 *
 * Asserted rather than parsed, this has a specific failure: if the scalar arrives as a JSON
 * *string* then `imageUrls` is truthy, `.front` is undefined, and `Object.values(...)[0]` is the
 * first character — so the card renders `<img src="h">` instead of the placeholder icon, with
 * nothing logged.
 */
export const parseImageUrls = (value: unknown): ImageUrlMap | null => {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return null;
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .filter((entry): entry is [string, string] => typeof entry[1] === 'string')
  );
};

/** The inventory item behind a trade-in item, with the `imageUrls` scalar narrowed. */
export type TradeinInventoryItem = Omit<RewareAdminTypes.InventoryItemAdmin, 'imageUrls'> & {
  imageUrls?: ImageUrlMap | null;
};

export type TradeinItem = Omit<RewareAdminTypes.TradeinItemTypeAdmin, 'inventoryItem'> & {
  inventoryItem?: TradeinInventoryItem | null;
};

/**
 * One trade-in as it arrives, before the boundary parse. Narrowed on `giftcards` for the same
 * reason {@link Giftcard} is: the generated `TradeinTypeAdmin` carries the full `GiftcardAdmin`,
 * so without this `page.rows[0].giftcards[0].number` type-checks — inside the very feature whose
 * comments say that value must never reach the browser.
 */
export type RawTradein = Omit<
  RewareAdminTypes.TradeinTypeAdmin,
  'giftcards' | 'tradeinItems' | 'sendbackTradeinItems'
> & {
  giftcards?: (Giftcard | null)[] | null;
  tradeinItems?: (TradeinItem | null)[] | null;
  sendbackTradeinItems?: (TradeinItem | null)[] | null;
};

/**
 * One trade-in as the page renders it: the raw row with `deliveryAddress` replaced by the parsed
 * form. The raw field is an opaque JSON scalar (`any`), so leaving it in place would silently
 * un-type every `tradein.deliveryAddress?.x` read on the page.
 */
export type TradeinRecord = Omit<RawTradein, 'deliveryAddress'> & {
  deliveryAddress: DeliveryAddress | null;
};

/**
 * The page envelope as it arrives — every field nullable, rows a nullable list of nullable
 * elements. See {@link TradeinsResult} for the validated form `fetchTradeins` hands back.
 */
export type TradeinsPage = Omit<RewareAdminTypes.PagedTradeinTypeAdmin, 'rows'> & {
  rows?: (RawTradein | null)[] | null;
};

/**
 * What `fetchTradeins` returns once the envelope has been validated: the narrowing the wire type
 * cannot express. Keeping the two distinct is what stops callers re-deriving the null handling.
 */
export interface TradeinsResult {
  count: number;
  rows: TradeinRecord[];
}

/**
 * Drop the nulls that every `[Type]` list in the admin schema can contain, and treat an absent
 * list as empty.
 *
 * Use this at each consumption site rather than re-asserting, and derive any count you render
 * from the filtered list — a tab badge taken from the raw list disagrees with the rows beneath
 * it, and one null element thrown into a `.map` takes down the panel.
 */
export const present = <T>(list: (T | null)[] | null | undefined): T[] =>
  (list ?? []).filter((item): item is T => item != null);

/**
 * reware-api's tri-state filter encoding: 1 = yes, 2 = no, omitted = either. Not a boolean, and
 * not 1/0 — the resolver tests `=== 1` / `=== 2`, so a 0 matches neither branch and the filter is
 * silently ignored while the UI still shows the chip. The generated args type this as a plain
 * `Int`; this narrows it to the two values the resolver actually reads.
 */
export type TriState = 1 | 2;

/**
 * Mirrors the generated `tradeins` query arguments, with the two fields we constrain further and
 * the three we always send made required. Deriving this from the args type is what guarantees
 * every filter name is one the query actually accepts — the hand-written version carried a
 * `tradeinNumber` that does not exist, which graphql-js dropped at coercion so the page came
 * back unfiltered while the UI still showed the chip.
 */
export interface FetchTradeinsFilters
  extends Pick<
    RewareAdminTypes.AdminQueriesTradeinsArgs,
    | 'state'
    | 'uuidPrefix'
    | 'id'
    | 'tradeinPpid'
    | 'tradeinSource'
    | 'email'
    | 'firstName'
    | 'lastName'
    | 'city'
    | 'postalCode'
    | 'countryCode'
    | 'inventoryItemId'
    | 'troveSerialNumber'
    | 'needsTroveAuthentication'
    | 'sendbackState'
  > {
  partnerId: number;
  page: number;
  /**
   * Always send it. The server defaults to 20 while react-admin's rows-per-page selector offers
   * 10/25/50, so omitting it makes the pager promise rows the query never asked for.
   */
  pageSize: number;
  hasEscalations?: TriState;
}

export interface TradeinConstants {
  states: ConstantOption[];
  sources: ConstantOption[];
  sendbackStates: ConstantOption[];
  conditions: ConstantOption[];
  rejectReasons: ConstantOption[];
  giftcardReasons: ConstantOption[];
}

/** A constant as it arrives: `ConstantsDataAdmin` has both fields nullable. */
export type RawConstantOption = Pick<RewareAdminTypes.ConstantsDataAdmin, 'code' | 'displayName'>;

/**
 * What the constants query may actually return. Every `ConstantsDataAdmin` list is a nullable
 * list of nullable elements whose own fields are nullable, so nothing here is guaranteed. The
 * hook's `usableConstants` narrows this to {@link TradeinConstants}; nothing else should assume
 * it has.
 */
export type RawTradeinConstants = {
  [K in keyof TradeinConstants]: (RawConstantOption | null)[] | null;
};
